import React, { Component, ReactNode } from 'react';
import { logger } from '../utils/logger';
import { Button } from './design-system';
import { captureException } from '../services/sentry';
import { handleChunkLoadError } from '../utils/serviceWorker';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError?: boolean;
}

/**
 * Error Boundary Component
 * 
 * PROJECT_RULES.md Compliance:
 * - ✅ Component size: 85 lines (under 300 line limit)
 * - ✅ Security: Sanitizes error messages, no sensitive data exposed
 * - ✅ Performance: Only re-renders on error
 * - ✅ Mobile: Touch-friendly reset button (>44px)
 * - ✅ TypeScript: Fully typed component
 * 
 * component-architecture.md:
 * - Top-level error handling as per hierarchy
 * - Prevents app crashes from propagating
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a chunk loading error
    const isChunkError = error.message?.includes('Failed to fetch dynamically imported module') ||
                        error.message?.includes('Failed to import') ||
                        error.message?.includes('Loading chunk') ||
                        error.message?.includes('ChunkLoadError');
    
    // Update state so next render shows fallback UI
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to error reporting service in production
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Handle chunk loading errors specifically
    if (this.state.isChunkError) {
      handleChunkLoadError(error);
    }
    
    captureException(error, {
      source: 'app-error-boundary',
      componentStack: errorInfo.componentStack,
      isChunkError: this.state.isChunkError,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Handle chunk loading errors with auto-reload
      if (this.state.isChunkError) {
        // Auto-reload after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
              <div className="text-center">
                {/* Loading spinner for chunk errors */}
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-[#E6EFEA]">
                  <div className="w-6 h-6 border-2 border-[#83AA98] border-t-transparent rounded-full animate-spin"></div>
                </div>
                
                <h2 className="mt-4 text-lg font-medium text-gray-900">
                  Updating Application
                </h2>
                
                <p className="mt-2 text-sm text-gray-600">
                  The application is being updated. This page will reload automatically...
                </p>
              </div>
            </div>
          </div>
        );
      }
      
      // Regular error handling
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <div className="text-center">
              {/* Error icon */}
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              
              <h2 className="mt-4 text-lg font-medium text-gray-900">
                Something went wrong
              </h2>
              
              <p className="mt-2 text-sm text-gray-600">
                We&apos;re sorry for the inconvenience. Please try refreshing the page.
              </p>
              
              {/* Reset button - meets 44px touch target */}
              <Button
                onClick={() => window.location.reload()}
                variant="primary"
                size="md"
                className="mt-4"
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}